import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Platform } from "react-native";

type UseWebRecaptchaV3Options = {
  siteKey?: string;
  action: string;
};

// ── Module-level shared state (singleton script management) ──────────
let scriptElement: HTMLScriptElement | null = null;
let consumerCount = 0;
let scriptLoadPromise: Promise<void> | null = null;

/**
 * Loads the Google reCAPTCHA script (v2, render=explicit) into <head>
 * exactly once. Returns a promise that resolves when `grecaptcha` is available.
 */
function loadRecaptchaScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="recaptcha/api.js"]'
    );
    if (existing) {
      scriptElement = existing;
      if ((window as any).grecaptcha) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      scriptElement = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
    scriptElement = script;
  });

  return scriptLoadPromise;
}

/**
 * Removes the reCAPTCHA script and any badge/challenge elements
 * when the last consumer unmounts.
 */
function removeRecaptchaScript() {
  if (scriptElement?.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
  }
  scriptElement = null;
  scriptLoadPromise = null;

  document
    .querySelectorAll(".grecaptcha-badge")
    .forEach((el) => el.parentNode?.removeChild(el));
}

// ── Hook ─────────────────────────────────────────────────────────────

export const useWebRecaptchaV3 = ({ siteKey }: UseWebRecaptchaV3Options) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const tokenResolveRef = useRef<((token: string | null) => void) | null>(null);

  // Load script + render invisible widget into the container div
  useEffect(() => {
    if (Platform.OS !== "web" || !siteKey) return;

    if (!document.getElementById("recaptcha-badge-style")) {
      const style = document.createElement("style");
      style.id = "recaptcha-badge-style";
      style.innerHTML = `
      .grecaptcha-badge {
        visibility: visible !important;
        opacity: 1 !important;
        z-index: 2147483647 !important;
      }
    `;
      document.head.appendChild(style);
    }

    consumerCount++;

    loadRecaptchaScript()
      .then(() => {
        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha || !containerRef.current) return;
        if (widgetIdRef.current !== null) return; // already rendered

        grecaptcha.ready(() => {
          if (!containerRef.current || widgetIdRef.current !== null) return;

          widgetIdRef.current = grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            size: "invisible",
            callback: (token: string) => {
              if (tokenResolveRef.current) {
                tokenResolveRef.current(token);
                tokenResolveRef.current = null;
              }
            },
            "expired-callback": () => {
              if (tokenResolveRef.current) {
                tokenResolveRef.current(null);
                tokenResolveRef.current = null;
              }
            },
            "error-callback": () => {
              if (tokenResolveRef.current) {
                tokenResolveRef.current(null);
                tokenResolveRef.current = null;
              }
            },
          });
        });
      })
      .catch((err) => {
        console.warn("[useWebRecaptcha] Script load error:", err);
      });

    return () => {
      if (widgetIdRef.current !== null) {
        try {
          (window as any).grecaptcha?.reset(widgetIdRef.current);
        } catch {
          // widget may already be gone
        }
        widgetIdRef.current = null;
      }

      consumerCount--;
      if (consumerCount <= 0) {
        consumerCount = 0;
        removeRecaptchaScript();
      }
    };
  }, [siteKey]);

  /**
   * Triggers the invisible reCAPTCHA check.
   * - If Google is satisfied → resolves with the token immediately.
   * - If Google is suspicious → shows a visual challenge; resolves
   *   with the token after the user completes it.
   * - On error / expiry → resolves with null.
   */
  const getToken = useCallback((): Promise<string | null> => {
    if (Platform.OS !== "web") return Promise.resolve(null);
    if (widgetIdRef.current === null) return Promise.resolve(null);

    // Cancel any pending request
    if (tokenResolveRef.current) {
      tokenResolveRef.current(null);
    }

    return new Promise<string | null>((resolve) => {
      tokenResolveRef.current = resolve;

      try {
        const grecaptcha = (window as any).grecaptcha;
        // Reset so the widget can be executed again (tokens are single-use)
        grecaptcha.reset(widgetIdRef.current);
        // Trigger the invisible verification (may show challenge)
        grecaptcha.execute(widgetIdRef.current);
      } catch (err) {
        console.warn("[useWebRecaptcha] execute error:", err);
        tokenResolveRef.current = null;
        resolve(null);
      }
    });
  }, []);

  /**
   * Container div that the invisible reCAPTCHA widget renders into.
   * Google will also use this anchor to overlay a visual challenge
   * when needed. Returns null on native platforms.
   */
  const RecaptchaIframe = useMemo(() => {
    if (Platform.OS !== "web" || !siteKey) return null;
    return React.createElement("div", {
      ref: containerRef,
      id: "recaptcha-container",
    });
  }, [siteKey]);

  return { getToken, RecaptchaIframe };
};
